/* eslint-disable max-len */
import { ReactElement, useState, createContext, useContext, useEffect } from 'react';
import FormDateSelector from 'src/components/FormInputs/FormDateSelector/FormDateSelector';
import FormDropdown from 'src/components/FormInputs/FormDropdown/FormDropdown';
import FormInputLabel from 'src/components/FormInputs/FormInputLabel/FormInputLabel';
import {
  MAX_DATE,
  SENIOR_SNAPSHOT_US_HISTORY_HIDE_YEAR,
} from 'src/types/Constants';
import { isDistrictUser, isSPO } from 'src/utilities/userUtilities';
import FormTextbox from '../../../../components/FormInputs/FormTextbox/FormTextbox';
import ManualInputPage from '../../../../components/ManualInputPage/ManualInputPage';
import Panel from '../../../../components/Panel/Panel';
import { useAppSelector } from '../../../../redux/hooks';
import {
  clearSeniorSnapshot,
  saveSeniorSnapshot,
  selectSeniorSnapshotDataEntryMode,
  selectSeniorSnapshotFormViewModel,
  selectSeniorSnapshotLoadingFormViewModel,
  selectSeniorSnapshotLoadingSeniorSnapshot,
  selectSeniorSnapshotSeniorSnapshot,
  selectSeniorSnapshotStudentFiles,
  setSeniorSnapshotDataEntryMode,
} from '../../../../redux/slices/seniorSnapshotSlice';
import {
  fetchSeniorSnapshotById,
  fetchSeniorSnapshotFormViewModel,
} from '../../../../redux/thunks/seniorSnapshotThunks';
import { appRoutePaths } from '../../../../services/route.service';
import {
  createSeniorSnapshot,
  updateSeniorSnapshot,
} from '../../../../services/SeniorSnapshot.service';
import { GLOBAL_DEBUG_MODE } from '../../../../testing/debugFunctions';
import { DataEntryMode } from '../../../../types/DataEntryMode';
import { Tabs_STUDENT } from '../../../../types/NavigationTabs';
import { PanelType } from '../../../../types/PanelType';
import { FormDropdownChangeEventConfig } from '../../../../types/propTypes/FormDropdownPropTypes';
import { ManualInputCrudAction } from '../../../../types/propTypes/ManualInputPagePropTypes';
import { SeniorSnapshotFormViewModel } from '../../../../types/SeniorSnapshotFormViewModel';
import { SeniorSnapshotModel } from '../../../../types/SeniorSnapshotModel';
import { SeniorSnapshotValidationSchema } from '../../../../types/Validation/SeniorSnapshotValidationSchema';
import {
  formatDateToMMDDYYYY,
  formatDateToYYYYMMDD,
} from '../../../../utilities/dateUtilities';
import { buildUserAccessNavigationTabItems } from '../../../../utilities/routeUtilities';
import './SeniorSnapshotInput.css';
import SeniorSnapshotUploadDocuments from './SeniorSnapshotUploadDocuments';

interface DirtyStateContextType {
  userHasMadeChanges: boolean;
  setUserHasMadeChanges: (value: boolean) => void;
  isDataLoading: boolean;
  setIsDataLoading: (value: boolean) => void;
}

const DirtyStateContext = createContext<DirtyStateContextType | undefined>(
  undefined
);

export const useDirtyState: () => DirtyStateContextType = () => {
  const context = useContext(DirtyStateContext);
  if (!context) {
    return {
      userHasMadeChanges: false,
      setUserHasMadeChanges: () => { /* noop */ },
      isDataLoading: false,
      setIsDataLoading: () => { /* noop */ },
    };
  }
  return context;
};

const SeniorSnapshotInput = (): ReactElement => {
  const [isAlgebraTransferDateRequired, setIsAlgebraTransferDateRequired] =
    useState(false);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const customDirtyStateHandler = (dirty: boolean, hasAnyFieldBeenTouched?: boolean): boolean => {
    return dirty && !!hasAnyFieldBeenTouched && !isDataLoading;
  };
  const [isEnglishTransferDateRequired, setIsEnglishTransferDateRequired] =
    useState(false);
  const [isBiologyTransferDateRequired, setIsBiologyTransferDateRequired] =
    useState(false);
  const [isHistoryTransferDateRequired, setIsHistoryTransferDateRequired] =
    useState(false);
  const [isReasonForDenialDisabled, setIsReasonForDenialDisabled] =
    useState(true);
  const [isDenialReasonRequired, setIsDenialReasonRequired] = useState(false);
  const seniorSnapshotModel: SeniorSnapshotModel = useAppSelector(
    selectSeniorSnapshotSeniorSnapshot
  );
  const formViewModel: SeniorSnapshotFormViewModel = useAppSelector(
    selectSeniorSnapshotFormViewModel
  );
  const isLoadingFormViewModel = useAppSelector(
    selectSeniorSnapshotLoadingFormViewModel
  );
  const isLoadingSeniorSnapshotModel = useAppSelector(
    selectSeniorSnapshotLoadingSeniorSnapshot
  );
  const dataEntryMode: DataEntryMode = useAppSelector(
    selectSeniorSnapshotDataEntryMode
  );
  const studentFiles = useAppSelector(selectSeniorSnapshotStudentFiles);
  const isLoading = isLoadingSeniorSnapshotModel || isLoadingFormViewModel;
  const supportedNonGenericErrors = [
    {
      statusCode: 400,
    },
  ];
  const [userHasMadeChanges, setUserHasMadeChanges] = useState<boolean>(false);
  const [previousModelId, setPreviousModelId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (seniorSnapshotModel.id !== previousModelId) {
      setUserHasMadeChanges(false);
      setIsDataLoading(true);
      setPreviousModelId(seniorSnapshotModel.id);
    }
  }, [seniorSnapshotModel.id, previousModelId]);

  useEffect(() => {
    if (isDataLoading && !isLoading) {
      setIsDataLoading(false);
    }
  }, [isDataLoading, isLoading]);

  const updateAlgebraTransferDateToRequired = async (
    config: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const isRequired =
      config.value === undefined || config.value === '' ? false : true;
    setIsAlgebraTransferDateRequired(isRequired);
  };

  const updateEnglishTransferDateToRequired = async (
    config: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const isRequired =
      config.value === undefined || config.value === '' ? false : true;
    setIsEnglishTransferDateRequired(isRequired);
  };

  const updateBiologyTransferDateToRequired = async (
    config: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const isRequired =
      config.value === undefined || config.value === '' ? false : true;
    setIsBiologyTransferDateRequired(isRequired);
  };

  const updateHistoryTransferDateToRequired = async (
    config: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const isRequired =
      config.value === undefined || config.value === '' ? false : true;
    setIsHistoryTransferDateRequired(isRequired);
  };

  const getAlgebraTrasferDateRequiredLabel = (): string => {
    return isAlgebraTransferDateRequired ? 'forced-required-label' : '';
  };

  const getEnglishTrasferDateRequiredLabel = (): string => {
    return isEnglishTransferDateRequired ? 'forced-required-label' : '';
  };

  const getBiologyTrasferDateRequiredLabel = (): string => {
    return isBiologyTransferDateRequired ? 'forced-required-label' : '';
  };

  const getHistoryTrasferDateRequiredLabel = (): string => {
    return isHistoryTransferDateRequired ? 'forced-required-label' : '';
  };

  const updateReasonForDenial = async (
    config: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const isDisabled = config.value === 'Denied' ? false : true;
    setIsReasonForDenialDisabled(isDisabled);

    if (config.value === 'Denied') {
      setIsDenialReasonRequired(true);
    } else {
      setIsDenialReasonRequired(false);
    }

    /* istanbul ignore next */
    if (config.setValueHook && isDisabled) {
      config.setValueHook('denialReason', '');
    }
  };

  return (
    <DirtyStateContext.Provider
      value={{
        userHasMadeChanges,
        setUserHasMadeChanges,
        isDataLoading,
        setIsDataLoading,
      }}
    >
      <ManualInputPage<SeniorSnapshotModel, SeniorSnapshotFormViewModel>
        customDirtyStateHandler={customDirtyStateHandler}
        actions={ManualInputCrudAction.CREATE_AND_EDIT}
        loadingOptions={{
          isLoading: isLoading,
          loadingDataId: 'senior-snapshot-input-loader',
        }}
        navigationTabs={buildUserAccessNavigationTabItems(Tabs_STUDENT)}
        classes={{ pageClass: 'data-input--senior-snapshot--input' }}
        parameterOptions={{
          routeParameterName: 'seniorSnapshotId',
          identityName: seniorSnapshotModel.id,
        }}
        routes={{
          backBarRoute: appRoutePaths.SeniorSnapshotList,
          cancelRoute: appRoutePaths.SeniorSnapshotList,
          createSuccessRoute: appRoutePaths.SeniorSnapshotInput,
        }}
        dataFunctions={{
          loadDataDetailById: fetchSeniorSnapshotById,
          loadFormDetail: fetchSeniorSnapshotFormViewModel,
          setEntryMode: setSeniorSnapshotDataEntryMode,
          setData: saveSeniorSnapshot,
          clearData: clearSeniorSnapshot,
          onCreate: (model) =>
            createSeniorSnapshot({
              ...model,
              birthDate: formatDateToYYYYMMDD(model.birthDate),
            }),
          onUpdate: (model) =>
            updateSeniorSnapshot({
              ...model,
              birthDate: formatDateToYYYYMMDD(model.birthDate),
            }),
        }}
        formDetails={{
          validationSchema: SeniorSnapshotValidationSchema,
          initialFormValues: {
            ...seniorSnapshotModel,
            birthDate: formatDateToMMDDYYYY(seniorSnapshotModel.birthDate),
            documentsUploadedToSharePoint:
              (studentFiles && studentFiles.length > 0) || false,
          },
          isFormModelEmpty: !formViewModel.defaultSchoolYear,
        }}
        config={{
          dataEntryMode: dataEntryMode,
          objectVerbiage: 'Senior Snapshot',
          pageHeader: 'Student',
          supportedNonGenericErrors: supportedNonGenericErrors,
        }}
        DEV_MODE={GLOBAL_DEBUG_MODE}
      >
        <div data-testid="senior-snapshot-input-child">
          <Panel panelType={PanelType.INFO} heading="Student Information">
            <div className="field-row field-row-1-1-2-1">
              <FormTextbox
                displayName="MSIS ID"
                field="studentIdentifierState"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
              <FormTextbox
                displayName="School Year"
                field="schoolYear"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
            </div>
            <div className="field-row">
              <FormTextbox
                displayName="Last Name"
                field="studentLastOrSurname"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
              <FormTextbox
                displayName="First Name"
                field="studentFirstName"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
              <FormTextbox
                displayName="Middle Name"
                field="studentMiddleName"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
              <FormTextbox
                displayName="Date of Birth"
                field="birthDate"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
              <FormTextbox
                displayName="SCD Indicator"
                field="scdIndicator"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
            </div>
          </Panel>
          <Panel panelType={PanelType.INFO} heading="LEA & School Information">
            <div className="field-row">
              <FormTextbox
                displayName="LEA"
                field="districtName"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
              <FormTextbox
                displayName="School"
                field="schoolName"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
            </div>
          </Panel>
          <Panel panelType={PanelType.INFO} heading="Student Demographics">
            <div className="field-row">
              <FormTextbox
                displayName="Gender"
                field="gender"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
              <FormTextbox
                displayName="Ethnicity"
                field="ethnicity"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
              <FormTextbox
                displayName="Race"
                field="race"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
            </div>
          </Panel>
          <Panel panelType={PanelType.INFO} heading="Senior Snapshot Updates">
            <div>
              <FormInputLabel
                field="seniorSnapshotUpdates"
                displayName="Algebra I"
              />
            </div>
            <div className="field-row">
              <FormTextbox
                displayName="Participated"
                field="algebraParticipated"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
              <FormDropdown
                displayName="Reason"
                field="algebraReason"
                isClearable={true}
                options={formViewModel.reasonOptions}
                onChangeEvent={updateAlgebraTransferDateToRequired}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
              />
              <FormDateSelector
                displayName="Transfer Date"
                field="algebraTransferDate"
                placeholderText="mm/dd/yyyy"
                validationSchema={SeniorSnapshotValidationSchema}
                classConfig={{
                  labelClasses: getAlgebraTrasferDateRequiredLabel(),
                }}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
                maxDate={MAX_DATE}
                isClearable
              />
              <FormTextbox
                displayName="Name of School Student Transferred from"
                field="algebraTransferredfromSchoolName"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
              />
              <FormDropdown
                displayName="Accredited School"
                field="algebraAccreditedSchool"
                options={formViewModel.accreditedSchoolOptions}
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
              />
            </div>
            <div>
              <FormInputLabel
                field="seniorSnapshotUpdates"
                displayName="English II"
              />
            </div>
            <div className="field-row">
              <FormTextbox
                displayName="Participated"
                field="englishParticipated"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
              <FormDropdown
                displayName="Reason"
                field="englishReason"
                isClearable={true}
                options={formViewModel.reasonOptions}
                onChangeEvent={updateEnglishTransferDateToRequired}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
              />
              <FormDateSelector
                displayName="Transfer Date"
                field="englishTransferDate"
                placeholderText="mm/dd/yyyy"
                validationSchema={SeniorSnapshotValidationSchema}
                classConfig={{
                  labelClasses: getEnglishTrasferDateRequiredLabel(),
                }}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
                maxDate={MAX_DATE}
                isClearable
              />
              <FormTextbox
                displayName="Name of School Student Transferred from"
                field="englishTransferredfromSchoolName"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
              />
              <FormDropdown
                displayName="Accredited School"
                field="englishAccreditedSchool"
                options={formViewModel.accreditedSchoolOptions}
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
              />
            </div>
            <div>
              <FormInputLabel
                field="seniorSnapshotUpdates"
                displayName="Biology"
              />
            </div>
            <div className="field-row">
              <FormTextbox
                displayName="Participated"
                field="biologyParticipated"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={true}
              />
              <FormDropdown
                displayName="Reason"
                field="biologyReason"
                isClearable={true}
                options={formViewModel.reasonOptions}
                onChangeEvent={updateBiologyTransferDateToRequired}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
              />
              <FormDateSelector
                displayName="Transfer Date"
                field="biologyTransferDate"
                placeholderText="mm/dd/yyyy"
                validationSchema={SeniorSnapshotValidationSchema}
                classConfig={{
                  labelClasses: getBiologyTrasferDateRequiredLabel(),
                }}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
                maxDate={MAX_DATE}
                isClearable
              />
              <FormTextbox
                displayName="Name of School Student Transferred from"
                field="biologyTransferredfromSchoolName"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
              />
              <FormDropdown
                displayName="Accredited School"
                field="biologyAccreditedSchool"
                options={formViewModel.accreditedSchoolOptions}
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
              />
            </div>
            {Number(seniorSnapshotModel.schoolYear) <
              SENIOR_SNAPSHOT_US_HISTORY_HIDE_YEAR && (
                <>
                  <div>
                    <FormInputLabel
                      field="seniorSnapshotUpdates"
                      displayName="US History"
                    />
                  </div>
                  <div className="field-row">
                    <FormTextbox
                      displayName="Participated"
                      field="historyParticipated"
                      validationSchema={SeniorSnapshotValidationSchema}
                      disabled={true}
                    />
                    <FormDropdown
                      displayName="Reason"
                      field="historyReason"
                      isClearable={true}
                      options={formViewModel.reasonOptions}
                      onChangeEvent={updateHistoryTransferDateToRequired}
                      disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
                    />
                    <FormDateSelector
                      displayName="Transfer Date"
                      field="historyTransferDate"
                      placeholderText="mm/dd/yyyy"
                      validationSchema={SeniorSnapshotValidationSchema}
                      classConfig={{
                        labelClasses: getHistoryTrasferDateRequiredLabel(),
                      }}
                      disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
                      maxDate={MAX_DATE}
                      isClearable
                    />
                    <FormTextbox
                      displayName="Name of School Student Transferred from"
                      field="historyNameofSchoolStudentTransferredfrom"
                      validationSchema={SeniorSnapshotValidationSchema}
                      disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
                    />
                    <FormDropdown
                      displayName="Accredited School"
                      field="historyAccreditedSchool"
                      options={formViewModel.accreditedSchoolOptions}
                      validationSchema={SeniorSnapshotValidationSchema}
                      disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
                    />
                  </div>
                </>
              )}
          </Panel>
          <SeniorSnapshotUploadDocuments
            schoolYear={seniorSnapshotModel.schoolYear}
            studentIdentifier={seniorSnapshotModel.studentIdentifierState}
            leaIdentifier={seniorSnapshotModel.leaIdentifier}
            studentFiles={studentFiles}
            disabled={isSPO() || !seniorSnapshotModel.studentUpdate}
            studentUpdate={seniorSnapshotModel.studentUpdate}
            reviewStatus={seniorSnapshotModel.reviewStatus}
          />
          <Panel panelType={PanelType.INFO} heading="MDE Use Only">
            <div className="field-row">
              {/* field "reviewStatus" will be disable by default, will be enabled only for users that are authorized to see. Should be handled when implementing permissions. And this is an optional field, otherwise form will be dirty and invalid state, and cannot create any record */}
              <FormDropdown
                displayName="Status (MDE Review)"
                field="reviewStatus"
                isClearable={true}
                options={formViewModel.reviewStatusOptions}
                onChangeEvent={updateReasonForDenial}
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={
                  isDistrictUser() || !seniorSnapshotModel.studentUpdate
                }
              />
              <FormTextbox
                displayName="Reason for Denial"
                field="denialReason"
                validationSchema={SeniorSnapshotValidationSchema}
                disabled={
                  isReasonForDenialDisabled ||
                  isDistrictUser() ||
                  !seniorSnapshotModel.studentUpdate
                }
                classConfig={{
                  labelClasses: isDenialReasonRequired
                    ? 'forced-required-label'
                    : '',
                }}
              />
            </div>
          </Panel>
        </div>
      </ManualInputPage>
    </DirtyStateContext.Provider>
  );
};
export default SeniorSnapshotInput;
